// src/constants/campaigns.js
// Per-audience content for the unlisted campaign landing pages.
// One shared <CampaignLanding> renders entirely from these objects.

// Shared founder note — edit in one place. Used by both pages.
// Add `photo: '/campaign/founder.jpg'` to show a headshot instead of initials.
export const FOUNDER = {
  name: 'Tim Kellermann',
  title: 'Founder, Heirloom',
  note: "I kept watching homeowners lose track of their home's history — and sellers, agents, and the pros who do the work all pay for it at the worst possible moment. Heirloom keeps a verified, sourced record of everything that happens to a home, so the right people can trust it when it counts. If that resonates, I'd love to have you in early.",
}

// Optional scheduler link (e.g. Calendly). Set this to show a "Book a 15-min
// call" CTA on the lead-form success screen; left empty it simply doesn't render.
const BOOKING_URL = ''

export const REALTOR = {
  audience: 'realtor',
  hero: {
    whoFor: 'For residential listing agents',
    headline: 'List homes that sell themselves.',
    sub: "Heirloom turns a home's maintenance history into buyer-ready seller disclosures — less liability, faster closings, happier sellers.",
    ctaLabel: 'Get early access',
    image: '/campaign/realtor-showings.webp',
  },
  trust: 'Sourced, defensible disclosures generated from a verified home history.',
  valueProps: [
    { title: 'Auto-generated seller disclosures', body: "AI drafts state-ready disclosures from the home's logged history — every claim traceable to its source." },
    { title: 'A home history buyers trust', body: 'Verified maintenance, appliances, and inspections that de-risk the sale.' },
    { title: 'Less liability, faster closings', body: 'Defensible, sourced disclosures mean fewer surprises and smoother deals.' },
  ],
  tour: {
    heading: 'See it in action',
    steps: [
      { image: '/campaign/realtor-record.webp', caption: 'A verified maintenance history — every system, every repair, every dollar.' },
      { image: '/campaign/realtor-disclosure.webp', caption: "Seller disclosures drafted from the home's real log — every line traceable to its source." },
      { image: '/campaign/realtor-showings.webp', caption: 'Send a guided disclosure to your seller and track each one from request to ready — no account needed on their end.' },
    ],
  },
  midCta: { heading: 'Less time on disclosures. More time winning listings.', ctaLabel: 'Get early access' },
  // Add real testimonials to show a "What people are saying" section: { quote, name, role }.
  testimonials: [],
  howItWorks: [
    { title: 'Invite the seller', body: 'Send a secure link from your dashboard.' },
    { title: 'Seller fills in minutes', body: 'A guided questionnaire, no jargon.' },
    { title: 'Download the disclosure', body: 'Sourced, defensible, ready to file.' },
  ],
  faqs: [
    { q: 'Does my seller need a Heirloom account?', a: 'No. You send a secure link and your seller answers a short guided questionnaire — no signup required on their end.' },
    { q: 'How is the disclosure defensible?', a: "Heirloom drafts from the home's logged history and your saved disclosure template, with every line traceable to its source. You review and approve before anything is final." },
    { q: 'How long does it take?', a: 'Most disclosures are drafted in a few minutes once your seller responds.' },
    { q: 'What does it cost?', a: 'Free to get started during early access — full pricing is shown when you create your account.' },
    { q: 'Is my data private?', a: 'Your data is encrypted and never sold, and you control who can see each property’s records.' },
  ],
  founder: FOUNDER,
  form: {
    heading: 'Get early access',
    sub: "See how Heirloom can win you listings. We'll reach out shortly.",
    reassurance: "Free to start during early access · your sellers don't need an account · your data stays private.",
    successMessage: "Thanks — we'll be in touch at the email you provided.",
    bookingUrl: BOOKING_URL,
  },
}

export const CONTRACTOR = {
  audience: 'contractor',
  hero: {
    whoFor: 'For local home-service pros',
    headline: 'A steady stream of local jobs.',
    sub: 'Get matched to homeowners near you, build a verified-review profile, and win more work — all from one Heirloom listing.',
    ctaLabel: 'Get early access',
    image: '/campaign/contractor-hero.webp',
  },
  trust: 'Homeowner-verified reviews and warm local leads — no ad spend required.',
  valueProps: [
    { title: 'Local leads, not ad spend', body: 'Get matched to nearby homeowners who already need work done.' },
    { title: "Reviews you can't fake", body: 'Homeowner-verified reviews tied to real jobs build instant trust.' },
    { title: 'A profile that wins jobs', body: 'A polished, searchable listing with your portfolio and warranties.' },
  ],
  tour: {
    heading: 'See it in action',
    steps: [
      { image: '/campaign/contractor-jobs.webp', caption: 'Log every job and its warranty — linked to the homeowner who can verify it.' },
      { image: '/campaign/contractor-reviews.webp', caption: 'Earn homeowner-verified reviews tied to real, completed jobs.' },
      { image: '/campaign/contractor-analytics.webp', caption: 'Track your leads, conversions, and engagement at a glance.' },
    ],
  },
  midCta: { heading: 'Get matched to homeowners near you.', ctaLabel: 'Get early access' },
  // Add real testimonials to show a "What people are saying" section: { quote, name, role }.
  testimonials: [],
  howItWorks: [
    { title: 'Create your listing', body: 'Trade, service area, portfolio, warranties.' },
    { title: 'Get matched', body: 'Nearby homeowners find you in the directory.' },
    { title: 'Build your reputation', body: 'Verified reviews compound into more work.' },
  ],
  faqs: [
    { q: 'How do I get leads?', a: 'Homeowners near you find you in the directory, and jobs at Heirloom-registered addresses invite the homeowner to confirm — turning real work into verified reviews.' },
    { q: 'Can reviews be faked?', a: 'No. A review is only allowed from a homeowner whose property is linked to a job you completed — verified on our end, never self-claimed.' },
    { q: 'What does it cost?', a: '$35/month with a 14-day free trial. Cancel anytime.' },
    { q: "What if I'm not getting jobs yet?", a: 'Your listing keeps working in the background — a complete, searchable profile with your trades, service area, portfolio, and warranties.' },
    { q: 'Is my data private?', a: 'Your business profile is public by design; your account and billing details are private and never sold.' },
  ],
  founder: FOUNDER,
  form: {
    heading: 'Get early access',
    sub: "Start getting matched to local homeowners. We'll reach out shortly.",
    reassurance: '14-day free trial · cancel anytime · $35/mo after.',
    successMessage: "Thanks — we'll be in touch at the email you provided.",
    bookingUrl: BOOKING_URL,
  },
}

// Unlisted campaign routes — used to suppress global site chrome (nav/footer/
// feedback widget) so these focused marketing pages have no full site nav.
export const CAMPAIGN_PATHS = ['/for-realtors', '/for-contractors']
export const isCampaignPath = (pathname) => CAMPAIGN_PATHS.includes(pathname)
